<template>
  <BaseExpansionPanel
    bold-label
    :bordered="bordered"
    :caption="errorMessage"
    data-testid="toggle-list"
    :label="label"
    :sticky-header="stickyHeader"
  >
    <template #header="{ toggle }">
      <slot
        name="header"
        :toggle="toggle"
      />
    </template>

    <div class="tw:flex tw:flex-col tw:gap-2">
      <div>
        <Checkbox
          class="tw:font-bold"
          :color="color"
          data-testid="checkbox-all"
          dense
          :disabled="disabled"
          :indeterminate="selectAllIndeterminate"
          :label="$t('global.select_all')"
          :model-value="hasAllSelected"
          new-design
          @update:model-value="toggleAll"
        />
      </div>

      <div
        class="tw:flex tw:flex-col tw:gap-2"
        data-cy="checkbox-selector-list"
      >
        <div
          v-for="item in itemsMapped"
          :key="item.id"
          class="sub-item"
        >
          <Checkbox
            v-if="item.children.length === 0"
            :color="color"
            :data-cy="`checkbox-selector-item[${item.name}]`"
            :data-testid="`checkbox-${item.id}`"
            dense
            :disabled="disabled || item.disabled"
            :label="item.name"
            :model-value="item.selected"
            new-design
            @update:model-value="toggleChild(item)"
          >
            <template #label>
              {{ item.name }}
              <slot
                v-if="item.selected"
                name="item-description"
                :selected="item.selected"
                :total-children-selected="
                  item.children.filter(isSelected).length
                "
              />
            </template>
          </Checkbox>
          <BaseExpansionPanel
            v-else
            :data-cy="`checkbox-selector-item[${item.name}]`"
            :disabled="!allowChildSelection && !item.selected"
            :header-style="{ zIndex: 20, top: '44px' }"
            :label="item.name"
            :model-value="expandedItems.includes(item.id)"
            no-padding
            :sticky-header="stickyHeader"
            @update:model-value="updateExpandedItem($event, item)"
          >
            <template #header="{ toggle }">
              <div class="tw:flex tw:w-full tw:gap-2">
                <Checkbox
                  :color="color"
                  :data-testid="`checkbox-${item.id}`"
                  dense
                  :disabled="disabled || Boolean(item.disabled)"
                  :label="item.name"
                  :model-value="item.selected"
                  new-design
                  @update:model-value="toggleParent(item)"
                />

                <div
                  class="tw:flex-1"
                  @click="toggle"
                >
                  <template v-if="item.selected">
                    <slot
                      name="item-description"
                      :selected="item.selected"
                      :total-children-selected="
                        item.children.filter(isSelected).length
                      "
                    />
                  </template>
                </div>
              </div>
            </template>

            <template #default="{ expanded }">
              <div
                class="tw:bg-white tw:py-2 tw:pl-6"
                :class="{
                  'tw:sticky tw:top-[64px] tw:z-10': expanded && stickyHeader,
                }"
              >
                <InputText
                  v-model="itemsSearch[item.id]"
                  clearable
                  :data-testid="`search-${item.id}`"
                  new-design
                  :placeholder="
                    $t('base_checkbox_selector.search_item', {
                      itemName: item.name,
                    })
                  "
                >
                  <template #prepend>
                    <BaseIcon
                      class="tw:text-xl"
                      icon="search"
                    />
                  </template>
                </InputText>
              </div>

              <div
                v-if="item.childrenFiltered.length === 0"
                :data-testid="`no-results-${item.id}`"
              >
                {{
                  $t("base_checkbox_selector.no_results_found", {
                    itemSearch: item.search,
                  })
                }}
              </div>

              <div
                v-else
                class="tw:flex tw:flex-col tw:gap-2 tw:pl-6"
              >
                <Checkbox
                  v-if="!itemsSearch[item.id]"
                  class="tw:font-bold"
                  :color="color"
                  :data-testid="`checkbox-all-${item.id}`"
                  dense
                  :disabled="disabled"
                  :indeterminate="selectAllChildrenIndeterminate(item)"
                  :label="$t('global.select_all')"
                  :model-value="item.allChildrenSelected"
                  new-design
                  @update:model-value="toggleAllChildren(item)"
                />
                <Checkbox
                  v-for="itemChild in item.childrenFiltered"
                  :key="itemChild.id"
                  :color="color"
                  :data-testid="`checkbox-${itemChild.id}`"
                  dense
                  :disabled="disabled || item.disabled"
                  :label="itemChild.name"
                  :model-value="isSelected(itemChild)"
                  new-design
                  @update:model-value="toggleChild(itemChild)"
                />
              </div>
            </template>
          </BaseExpansionPanel>
        </div>
      </div>
    </div>

    <slot name="footer" />
  </BaseExpansionPanel>
</template>

<script setup lang="ts">
import { difference } from "lodash-es";
import { computed, onMounted, ref, watch } from "vue";

import {
  BaseExpansionPanel,
  BaseIcon,
  InputText,
} from "shared/components/base";
import Checkbox from "shared/components/core/forms/Checkbox.vue";
import type { MediumField } from "shared/helpers/media";

interface ItemsSearch {
  [key: number]: string;
}

export interface Item {
  id: number;
  name: string;
  children?: Item[];
  disabled?: boolean;
  allChildrenSelected?: boolean;
  childrenFiltered?: Item[];
}

const modelValue = defineModel<number[]>({
  default: () => [],
});

interface Props {
  color: "primary" | MediumField;
  label?: string;
  errorMessage?: string;
  items: Item[];
  disabled?: boolean;
  selectionMode?: "include" | "exclude";
  allowChildSelection?: boolean;
  bordered?: boolean;
  stickyHeader?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  label: "",
  errorMessage: "",
  selectionMode: "include",
});

// Model
const isIncludeMode = computed(() => props.selectionMode === "include");
const selectedItems = ref<number[]>(modelValue.value);

function removeIdsFromSelectedItems(ids: number[]): number[] {
  return selectedItems.value.filter((id) => !ids.includes(id));
}

function addIdsToSelectedItems(ids: number[]) {
  return [...selectedItems.value, ...ids];
}

function isSelected({ id }: { id: number }): boolean {
  const isInModelValue = selectedItems.value.includes(id);

  return isIncludeMode.value ? isInModelValue : !isInModelValue;
}

watch(
  () => selectedItems.value,
  (value) => {
    modelValue.value = value;
  }
);

watch(
  () => modelValue.value,
  () => {
    const diff = difference(selectedItems.value, modelValue.value);

    if (diff.length) {
      selectedItems.value = modelValue.value;
    }
  },
  { deep: true }
);

// Search
const itemsSearch = ref<ItemsSearch>({});

onMounted(() => {
  props.items.forEach((item) => {
    itemsSearch.value[item.id] = "";
  });
});

// Items
const itemsMapped = computed(() =>
  props.items.map((parent) => {
    const children = parent.children ?? [];
    const search = itemsSearch.value?.[parent.id] ?? "";

    const hasChildren = children.length > 0;
    const allChildrenSelected = hasChildren && children.every(isSelected);
    const selected = isSelected(parent) || allChildrenSelected;

    return {
      ...parent,
      children,
      selected,
      allChildrenSelected,
      search,
      childrenFiltered: children.filter(
        (child) =>
          !search || child.name.toLowerCase().includes(search.toLowerCase())
      ),
    };
  })
);

// Selection
const expandedItems = ref<number[]>([]);

const hasAllSelected = computed<boolean>(() =>
  itemsMapped.value.every((item) => item.selected)
);

const allItemIds = computed(() => {
  const ids: number[] = [];

  props.items.forEach((item) => {
    ids.push(item.id);

    if (item.children && item.children.length > 0) {
      item.children.forEach((child) => ids.push(child.id));
    }
  });

  return ids;
});

const selectAllIndeterminate = computed<boolean>(() => {
  if (hasAllSelected.value) return false;

  return itemsMapped.value.some((item) => item.selected);
});

function toggleAll() {
  if (hasAllSelected.value) {
    selectedItems.value = isIncludeMode.value ? [] : allItemIds.value;
  } else {
    selectedItems.value = isIncludeMode.value ? allItemIds.value : [];
  }
}

function getItemIds(itemMapped: Item) {
  const ids = [itemMapped.id];
  const childrenIds = itemMapped.children?.map((child) => child.id) || [];

  return { ids, childrenIds };
}

function toggleParent(itemMapped: Item) {
  const isItemSelected = isSelected(itemMapped);
  const { ids, childrenIds } = getItemIds(itemMapped);

  const allIds = [...ids, ...childrenIds];

  if (isItemSelected) {
    selectedItems.value = isIncludeMode.value
      ? removeIdsFromSelectedItems(allIds)
      : addIdsToSelectedItems(allIds);
  } else {
    selectedItems.value = isIncludeMode.value
      ? addIdsToSelectedItems(allIds)
      : removeIdsFromSelectedItems(allIds);
  }

  if (isItemSelected) {
    expandedItems.value = expandedItems.value.filter(
      (id) => id !== itemMapped.id
    );
  } else if (!isItemSelected) {
    expandedItems.value.push(itemMapped.id);
  }
}

function toggleAllChildren(itemMapped: Item) {
  const { childrenIds } = getItemIds(itemMapped);

  if (itemMapped.allChildrenSelected) {
    selectedItems.value = isIncludeMode.value
      ? removeIdsFromSelectedItems(childrenIds)
      : addIdsToSelectedItems(childrenIds);
  } else {
    selectedItems.value = isIncludeMode.value
      ? addIdsToSelectedItems(childrenIds)
      : [];
  }
}

function selectAllChildrenIndeterminate(itemMapped: Item) {
  if (itemMapped.allChildrenSelected) return false;

  const selectedChildren = itemMapped.childrenFiltered?.filter((itemChild) =>
    isSelected(itemChild)
  );

  return Boolean(selectedChildren && selectedChildren.length);
}

function toggleChild(child: Item) {
  if (isSelected(child)) {
    selectedItems.value = isIncludeMode.value
      ? removeIdsFromSelectedItems([child.id])
      : addIdsToSelectedItems([child.id]);
  } else {
    selectedItems.value = isIncludeMode.value
      ? addIdsToSelectedItems([child.id])
      : removeIdsFromSelectedItems([child.id]);
  }
}

function updateExpandedItem(expanded: boolean, item: Item) {
  const isExpanded = expandedItems.value.includes(item.id);
  if (expanded === isExpanded) return;

  if (expanded && isExpanded) {
    expandedItems.value = expandedItems.value.filter((id) => id !== item.id);
  } else if (!isExpanded) {
    expandedItems.value.push(item.id);
  }
}
</script>
